import React from "react";
import {
    ChevronUp,
    ChevronDown,
    ChevronsLeft,
    ChevronsRight,
    ChevronLeft,
    ChevronRight,
    Search,
    Download,
    Info,
} from "lucide-react";
import { Combobox } from "components/ui/combobox";
import { Skeleton } from "components/ui/skeleton";
import { useSmoothLoading } from "lib/use-smooth-loading";
import ChargeBreakdownSheet from "./ChargeBreakdownSheet";
import type { BranchCnDetailsRow } from "../api/api";

export interface BranchCnRow {
    cn_number: string;
    cn_date: string;
    consignor?: string;
    consignee?: string;
    origin?: string;
    destination?: string;
    status: string;
    value: number;
    transit_days?: number;
    delivery_to_pod?: number;
    pod_to_bill?: number;
    [chargeHead: string]: string | number | undefined;
}

interface Props {
    data: BranchCnRow[];
    loading: boolean;
    page: number;
    pageSize: number;
    totalItems: number;
    sortBy: string;
    sortOrder: "asc" | "desc";
    search: string;
    status?: string | null;
    customerName: string;
    branchName?: string | null;
    onSearchChange: (val: string) => void;
    onStatusChange: (val: string | null) => void;
    onSortChange: (col: string) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    onBack: () => void;
    onDownloadExcel: () => void;
    downloadingExcel?: boolean;
}

const columns: Array<{
    key: string
    label: string
    isNumeric?: boolean
}> = [
    { key: "cn_number", label: "CN Number" },
    { key: "cn_date", label: "CN Date" },
    { key: "consignor", label: "Consignor" },
    { key: "consignee", label: "Consignee" },
    { key: "origin", label: "Origin" },
    { key: "destination", label: "Destination" },
    { key: "status", label: "Status" },
    { key: "value", label: "Value", isNumeric: true },
    { key: "transit_days", label: "Transit Days", isNumeric: true },
    { key: "delivery_to_pod", label: "Delivery to POD", isNumeric: true },
    { key: "pod_to_bill", label: "POD to Bill", isNumeric: true },
];

const statusOptions = [
    { value: "", label: "All Status" },
    { value: "in_transit", label: "In Transit" },
    { value: "in_stock", label: "In Stock" },
    { value: "delivered_lte_10_days", label: "Delivered <= 10" },
    { value: "delivered_gt_10_days", label: "Delivered > 10" },
    { value: "pod_received_lte_10_days", label: "POD Received <= 10" },
    { value: "pod_received_gt_10_days", label: "POD Received > 10" },
];

const statusLabelMap: Record<string, string> = {
    in_transit: "In Transit",
    in_stock: "In Stock",
    delivered: "Delivered",
    pod_received: "POD Received",
    delivered_lte_10_days: "Delivered <= 10",
    delivered_gt_10_days: "Delivered > 10",
    pod_received_lte_10_days: "POD Received <= 10",
    pod_received_gt_10_days: "POD Received > 10",
};

const BranchCnDetailsTable: React.FC<Props> = ({
    data,
    loading,
    page,
    pageSize,
    totalItems,
    sortBy,
    sortOrder,
    search,
    status,
    customerName,
    branchName,
    onSearchChange,
    onStatusChange,
    onSortChange,
    onPageChange,
    onPageSizeChange,
    onBack,
    onDownloadExcel,
    downloadingExcel = false,
}) => {

    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const { isLoading } = useSmoothLoading(loading, {
        minLoadingTime: 150,
        debounceTime: 150,
    });

    const [selectedCn, setSelectedCn] = React.useState<BranchCnDetailsRow | null>(null);
    const [sheetOpen, setSheetOpen] = React.useState(false);

    const openBreakdown = (row: BranchCnRow) => {
        setSelectedCn(row as BranchCnDetailsRow);
        setSheetOpen(true);
    };

    const formatDate = (value?: string) => {
        if (!value) return "-";
        return new Date(value).toLocaleDateString("en-IN");
    };

    const formatNumber = (value?: number) => {
        if (value === null || value === undefined) return "-";
        return value.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const renderSkeletonRows = () =>
        Array.from({ length: 12 }).map((_, i) => (
            <tr key={i}>
                {columns.map((_, j) => (
                    <td key={j} className="border px-2 py-1.5">
                        <Skeleton className="h-4 w-full" />
                    </td>
                ))}
            </tr>
        ));

    return (
        <div className="flex flex-col h-full">
            <div className="shrink-0 space-y-4 pb-4">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={onBack}
                            className="px-3 py-1 text-sm rounded bg-gray-200 hover:bg-gray-300"
                        >
                            ← Back
                        </button>

                        <h2 className="text-lg font-semibold">Customer: {customerName}</h2>
                        <h2 className="text-lg font-semibold">Branch: {branchName}</h2>
                    </div>
                </div>

                <div className="flex items-end justify-between gap-6">
                    <div className="flex items-end gap-6">
                        <div>
                            <label className="block text-sm font-medium mb-1">Search CN</label>
                            <div className="relative w-64">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <input
                                    value={search}
                                    onChange={(e) => onSearchChange(e.target.value)}
                                    placeholder="Search CN / consignor / consignee"
                                    className="w-full h-10 rounded-md border pl-9 pr-3 text-sm"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Status</label>
                            <div className="w-56">
                                <Combobox
                                    options={statusOptions}
                                    value={status ?? ""}
                                    onValueChange={(val) => {
                                        if (!val) onStatusChange(null);
                                        else onStatusChange(val);
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={onDownloadExcel}
                        disabled={downloadingExcel}
                        className="px-3 py-1 text-sm rounded-md border border-green-600 text-green-700 hover:bg-green-50 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Download size={16} />
                        {downloadingExcel ? "Downloading..." : "Excel"}
                    </button>
                </div>
            </div>

            <div className="border border-gray-200 overflow-auto flex-1">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="bg-gray-100">
                            {columns.map((col) => {
                                const active = sortBy === col.key;
                                return (
                                    <th
                                        key={col.key}
                                        onClick={() => col.key !== "status" && onSortChange(col.key)}
                                        className={`border border-gray-300 px-2 py-2 whitespace-nowrap font-semibold
                                        ${col.key !== "status" ? "cursor-pointer" : ""}
                                        ${col.isNumeric ? "text-right" : "text-left"}`}
                                    >
                                        <div className="flex items-center gap-1">
                                            {col.label}
                                            {col.key !== "status" && (
                                                active ? (
                                                    sortOrder === "asc"
                                                        ? <ChevronUp size={14} />
                                                        : <ChevronDown size={14} />
                                                ) : (
                                                    <ChevronUp size={14} className="text-gray-300" />
                                                )
                                            )}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {isLoading ? (
                            renderSkeletonRows()
                        ) : data.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={columns.length}
                                    className="text-center py-16 text-gray-500"
                                >
                                    No CN data available
                                </td>
                            </tr>
                        ) : (
                            data.map((row, idx) => (
                                <tr
                                    key={idx}
                                    className="hover:bg-gray-50"
                                >
                                    {columns.map((col) => {
                                        let value: any = row[col.key];
                                        if (col.key === "status") {
                                            value = statusLabelMap[value] ?? value;
                                        }

                                        if (col.key === "cn_date") {
                                            value = formatDate(value);
                                        }

                                        if (col.key === "value") {
                                            value = formatNumber(value);
                                        }

                                        if (
                                            col.key === "transit_days" ||
                                            col.key === "delivery_to_pod" ||
                                            col.key === "pod_to_bill"
                                        ) {
                                            value = value ?? "-";
                                        }

                                        if (col.key === "value") {
                                            return (
                                                <td
                                                    key={col.key}
                                                    className="border border-gray-200 px-2 py-1.5 whitespace-nowrap text-right"
                                                >
                                                    <div className="flex items-center justify-end gap-1.5">
                                                        <span>{value || "-"}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => openBreakdown(row)}
                                                            className="inline-flex items-center justify-center h-5 w-5 rounded text-gray-400 hover:text-gray-900 hover:bg-gray-100"
                                                            aria-label={`View charge breakdown for ${row.cn_number}`}
                                                            title="View charge breakdown"
                                                        >
                                                            <Info size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            );
                                        }

                                        return (
                                            <td
                                                key={col.key}
                                                className={`border border-gray-200 px-2 py-1.5 whitespace-nowrap
                                                ${col.isNumeric ? "text-right" : "text-left"}`}
                                            >
                                                {value || "-"}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between pt-4">
                <span className="text-sm text-gray-600">
                    Showing <strong>{totalItems === 0 ? 0 : (page - 1) * pageSize + 1}</strong> to{" "}
                    <strong>{Math.min(page * pageSize, totalItems)}</strong> of{" "}
                    <strong>{totalItems}</strong>
                </span>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm">
                        Rows per page
                        <select
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                            className="border rounded px-2 py-1"
                        >
                            {[10, 20, 50, 100].map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            disabled={page === 1}
                            onClick={() => onPageChange(1)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronsLeft size={16} />
                        </button>
                        <button
                            disabled={page === 1}
                            onClick={() => onPageChange(page - 1)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <span className="px-2 text-sm">
                            Page <strong>{page}</strong> / {totalPages}
                        </span>
                        <button
                            disabled={page === totalPages}
                            onClick={() => onPageChange(page + 1)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronRight size={16} />
                        </button>
                        <button
                            disabled={page === totalPages}
                            onClick={() => onPageChange(totalPages)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronsRight size={16} />
                        </button>
                    </div>
                </div>
            </div>

            <ChargeBreakdownSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                cn={selectedCn}
            />
        </div>
    );
};

export default BranchCnDetailsTable;